import { COLLECTIONS, type CollectionSlug } from '@/features/metadata-library/config/collections';
import { METADATA_LIBRARY_SKELETON_ROWS } from '@/features/metadata-library/config/constants';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useIsMobile } from '@/shared/hooks/useIsMobile';

/**
 * Cold-load placeholder for a library list, shaped like the table that replaces
 * it: the same column count (id + the collection's columns + the actions cell) and
 * a first screen of rows, so the layout does not jump when the records arrive.
 */
function MetadataLibraryTableSkeleton({ collection }: { collection: CollectionSlug }) {
  const isMobile = useIsMobile();

  if (isMobile) {
    const mobileSkeletonRows = Array.from(
      { length: METADATA_LIBRARY_SKELETON_ROWS },
      (_, row) => `mobile-skeleton-${row}`,
    );

    return (
      <div className="rounded-lg border" aria-hidden>
        {mobileSkeletonRows.map((rowKey) => (
          <div
            key={rowKey}
            className="flex items-start justify-between gap-2 border-b px-3 py-3 last:border-0"
          >
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3.5 w-56 max-w-full" />
            </div>
            <Skeleton className="size-7 shrink-0" />
          </div>
        ))}
      </div>
    );
  }

  const columnCount = COLLECTIONS[collection].columnKeys.length + 2;

  return (
    <div className="rounded-lg border" aria-hidden>
      <div className="flex gap-3 border-b bg-muted/40 px-3 py-2.5">
        {Array.from({ length: columnCount }, (_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder cells, no identity
          <Skeleton key={index} className="h-4 flex-1" />
        ))}
      </div>
      {Array.from({ length: METADATA_LIBRARY_SKELETON_ROWS }, (_, row) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows, no identity
        <div key={row} className="flex gap-3 border-b px-3 py-3 last:border-0">
          {Array.from({ length: columnCount }, (_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder cells, no identity
            <Skeleton key={index} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Cold-load placeholder for the collection page while its field catalog and
 * records resolve. It matches the in-shell page padding and heading/table shape.
 *
 * The collection is known from the route param even on a cold load, so the table
 * placeholder underneath already has the right column count.
 */
export function MetadataLibraryLayoutSkeleton({ collection }: { collection: CollectionSlug }) {
  return (
    <div className="flex flex-1 flex-col gap-3 p-4">
      <div className="flex items-center justify-between gap-3" aria-hidden>
        <Skeleton className="h-6 w-32" />
      </div>
      <MetadataLibraryTableSkeleton collection={collection} />
    </div>
  );
}

/**
 * Cold-load placeholder for an edit dialog, shaped to the form that is loading.
 *
 * Each collection's form has a known field count, so the dialog passes its own —
 * the placeholder then occupies roughly the height the real form will, and the
 * dialog does not visibly resize once the record arrives.
 */
export function LibraryRecordFormSkeleton({ fieldCount }: { fieldCount: number }) {
  return (
    <div className="max-w-2xl space-y-6" aria-hidden>
      <Skeleton className="h-7 w-48" />
      <div className="space-y-4">
        {Array.from({ length: fieldCount }, (_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder fields, no identity
          <div key={index} className="space-y-1.5">
            <Skeleton className="h-3.5 w-32" />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>
      <Skeleton className="h-9 w-28" />
    </div>
  );
}
